import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileText, Download, UserPlus, Stethoscope, MessageSquare, History, ChevronDown } from "lucide-react";
import api, { apiError, fmtDate, fmtDateTime, downloadBlob, istDateKey } from "../api";
import { Card, StatusBadge, Badge, Spinner } from "../components/ui";

function EventBody({ event }) {
  if (event.type === "registered") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <UserPlus size={15} className="text-navy" />
        <span className="text-sm font-bold text-slate-900">Candidate Registered</span>
        {event.detail && <span className="text-xs text-slate-500">{event.detail}</span>}
        <span className="ml-auto text-xs text-slate-400">{fmtDateTime(event.at)}</span>
      </div>
    );
  }
  if (event.type === "document") {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2">
        <FileText size={15} className="text-teal shrink-0" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{event.filename}</p>
          <p className="text-[11px] text-slate-500">{event.category_label} · Client: {event.client_name || "-"} · {fmtDateTime(event.at)}</p>
        </div>
        {event.superseded && <Badge color="slate">replaced later</Badge>}
        {event.downloadable !== false && (
          <button
            onClick={() => downloadBlob(`/documents/${event.doc_id}/download`, event.filename)}
            className="ml-auto shrink-0 rounded p-1.5 text-slate-500 transition-colors hover:bg-teal-light hover:text-teal"
            title="Download this report"
            data-testid={`download-history-doc-${event.doc_id}`}
          >
            <Download size={15} />
          </button>
        )}
      </div>
    );
  }
  if (event.type === "verdict") {
    return (
      <div className="rounded-md border border-teal/40 bg-teal-light/20 p-4" data-testid="timeline-verdict">
        <div className="flex flex-wrap items-center gap-2">
          <Stethoscope size={15} className="text-teal" />
          <span className="text-sm font-bold text-slate-900">Verdict</span>
          <StatusBadge status={event.status} />
          <span className="text-xs text-slate-500">by {event.by}</span>
          <span className="ml-auto text-xs text-slate-400">{fmtDateTime(event.at)}</span>
        </div>
        {event.remarks && <p className="mt-2 text-sm text-slate-700">{event.remarks}</p>}
        {event.repeat_tests && (
          <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800">
            Required repeat tests: {event.repeat_tests}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-start gap-2">
      <MessageSquare size={14} className="mt-0.5 shrink-0 text-slate-400" />
      <div>
        <p className="text-sm text-slate-700">{event.text}</p>
        <p className="text-[11px] text-slate-500">{event.by} · {event.role} · {fmtDateTime(event.at)}</p>
      </div>
    </div>
  );
}

export default function CandidateHistory() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openDate, setOpenDate] = useState(null);

  useEffect(() => {
    api.get(`/candidates/${id}/timeline`).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, [id]);

  const groups = useMemo(() => {
    if (!data) return [];
    const byDate = new Map();
    for (const e of data.events) {
      const key = istDateKey(e.at);
      if (!byDate.has(key)) byDate.set(key, []);
      byDate.get(key).push(e);
    }
    return Array.from(byDate.entries()).map(([key, evs]) => {
      const verdicts = evs.filter((ev) => ev.type === "verdict");
      const finalVerdict = verdicts.length ? verdicts[verdicts.length - 1] : null;
      return {
        key,
        at: evs[0].at,
        events: evs,
        verdict: finalVerdict,
        docCount: evs.filter((ev) => ev.type === "document").length,
        registered: evs.some((ev) => ev.type === "registered"),
      };
    }).reverse();
  }, [data]);

  if (error) return <p className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</p>;
  if (!data) return <div className="flex justify-center py-20"><Spinner /></div>;

  const { candidate } = data;
  return (
    <div className="max-w-3xl" data-testid="candidate-history-page">
      <Link to={`/candidates/${id}`} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-navy" data-testid="back-to-candidate-link">
        <ArrowLeft size={15} /> Back to candidate
      </Link>

      <div className="mb-2 flex flex-wrap items-center gap-3">
        <History size={22} className="text-navy" />
        <h1 className="font-heading text-3xl font-extrabold text-slate-900" data-testid="history-candidate-name">{candidate.name}</h1>
        <StatusBadge status={candidate.status} />
        <span className="text-sm text-slate-500">{candidate.rank} · ID {candidate.company_id}</span>
      </div>
      <p className="mb-6 text-sm text-slate-500">
        Previous history by date — click a date to see that day's reports and verdict.
      </p>

      {groups.length === 0 ? (
        <Card className="p-6"><p className="text-sm text-slate-400">No history yet.</p></Card>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const open = openDate === g.key;
            return (
              <Card key={g.key} className="overflow-hidden">
                <button
                  onClick={() => setOpenDate(open ? null : g.key)}
                  className="flex w-full flex-wrap items-center gap-3 px-6 py-4 text-left transition-colors hover:bg-slate-50"
                  data-testid={`history-date-${g.key.replaceAll(" ", "-")}`}
                >
                  <span className="font-heading text-base font-bold text-slate-900">{fmtDate(g.at)}</span>
                  {g.registered && <Badge color="navy">Registered</Badge>}
                  {g.docCount > 0 && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-slate-500">
                      <FileText size={12} className="text-teal" /> {g.docCount} report{g.docCount > 1 ? "s" : ""}
                    </span>
                  )}
                  <span className="ml-auto flex items-center gap-2">
                    {g.verdict ? <StatusBadge status={g.verdict.status} /> : <Badge color="slate">No verdict</Badge>}
                    <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                  </span>
                </button>
                {open && (
                  <div className="space-y-3 border-t border-slate-100 px-6 py-5" data-testid="history-date-detail">
                    {g.events.map((e, i) => <EventBody key={i} event={e} />)}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
